import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Flex, Text, cn } from '@kognitos/lattice';

import type { V1CodeManifest } from '@/generated/hooks/grimoire/models';
import { V1AutomationActivationState } from '@/generated/hooks/grimoire/models/v1AutomationActivationState';
import {
  AutomationStageBadge,
  type TAutomationStage,
} from '@/shared/components/AutomationStageBadge';
import { DeactivatedBadge } from '@/shared/components/DeactivatedBadge';
import { EditableTitle } from '@/shared/components/EditableTitle';
import { useRenameAutomationTitle } from '@/shared/hooks/use-rename-automation-title';
import { usePermissions } from '@/shared/permissions';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';
import { formatTimestamp } from '@/shared/utils/datetime-utils';

import { DocumentTitleMenu } from './DocumentTitleMenu';

/**
 * The live state of a run, when one is in flight. Replaces the stage's meta
 * text for as long as it is set — the run is the more urgent fact about the
 * document than when it was last saved.
 *
 * `step` is optional on the run-backed variants: a run that has not reported a
 * step yet shows the state without naming one, rather than claiming step 1 for
 * work that has not started.
 *
 * `step-test` is the sandboxed single-step workbench rather than a run of the
 * automation, so it names no step and carries no live dot.
 */
export type TAutomationRunningState =
  | { state: 'executing'; step?: number; totalSteps: number }
  | { state: 'awaiting-guidance'; step?: number }
  | { state: 'step-test' };

/**
 * Props for {@link AutomationDocumentTitle}.
 */
export interface IAutomationDocumentTitleProps {
  /** The automation this title names and acts on. */
  automationId?: string;
  /** The automation's display name — the only place it appears on the page. */
  title?: string;
  /** Lifecycle stage, which drives the badge. */
  stage?: TAutomationStage;
  /**
   * Allows click-to-edit on the title. Defaults to `false` so a read-only or
   * version-history viewer can never fire the rename mutation. `RenameAutomation`
   * is checked on top of this, so callers pass the mode, not the permission.
   */
  isTitleEditable?: boolean;
  /** Current version; the delete dialog scopes its active-run warning by it. */
  version?: string;
  /** Export the automation as a zip, offered in the options menu. */
  onExport?: () => void;
  /** The built automation's manifest, which enables "Export as Markdown". */
  exportManifest?: V1CodeManifest;
  /** Used for the Markdown export filename when the manifest carries no title. */
  exportTitleFallback?: string;
  /** Activation state, which dims the stage badge and adds a Deactivated one. */
  activationState?: V1AutomationActivationState;
  /** Classes for the editable title, e.g. its max width in the bar. */
  titleClassName?: string;
  /**
   * Renders the options menu beside the name. Turn it off where the caller
   * places a `DocumentTitleMenu` elsewhere and passes its renames through
   * `renameRequestCount`.
   */
  showMenu?: boolean;
  /** Bump to put the title into edit mode, e.g. from a menu rendered elsewhere. */
  renameRequestCount?: number;
  /** Replaces the stage badge, e.g. with a switch between the live version and the draft. */
  stageSlot?: ReactNode;
}

/**
 * The automation's name, its options menu and its stage badges. Shared by the
 * document title bar and the asset workspace's document heading, so a rename
 * from either invalidates the same caches and checks the same permission.
 */
export function AutomationDocumentTitle({
  automationId,
  title,
  stage = 'draft',
  isTitleEditable = false,
  version,
  onExport,
  exportManifest,
  exportTitleFallback,
  activationState,
  titleClassName,
  showMenu = true,
  renameRequestCount: externalRenameRequests = 0,
  stageSlot,
}: IAutomationDocumentTitleProps) {
  const location = useLocation();
  const navigate = useNavigate();
  // Bumped by the options menu's `Rename` to remount the title in edit mode.
  const [ownRenameRequests, setRenameRequestCount] = useState(0);
  const renameRequestCount = ownRenameRequests + externalRenameRequests;
  // Latch the request on the first render that carries it: clearing the
  // navigation state below must not retract it, so the ref — not
  // `location.state` — is what drives edit mode.
  const focusRequestedRef = useRef(false);
  if (
    isTitleEditable &&
    (location.state as { focusTitle?: boolean } | null)?.focusTitle
  ) {
    focusRequestedRef.current = true;
  }
  const shouldFocusTitle = focusRequestedRef.current;

  // Clear navigation state so back/forward or a reload doesn't re-trigger
  // focus. This has to go through the router: the data router keeps its own
  // in-memory copy of location.state and re-persists it on the next history
  // sync, so a raw window.history.replaceState is both ineffective (ENG-4145,
  // see src/pages/workspace/draft/edit/Page.tsx) and destructive — it discards
  // the router's own {usr, key, idx} entry along with the flag.
  // eslint-disable-next-line no-restricted-syntax -- reason: imperative router API; the flag has to be consumed once and cleared out of history, which is a side effect of arriving rather than derivable state
  useEffect(() => {
    if ((location.state as { focusTitle?: boolean } | null)?.focusTitle) {
      navigate(`${location.pathname}${location.search}${location.hash}`, {
        replace: true,
        state: null,
      });
    }
  }, [location, navigate]);

  // Shared with the page header's title so a rename from either place
  // invalidates the same caches and reports the same failure.
  const { rename: handleRename, isRenaming } =
    useRenameAutomationTitle(automationId);

  // Both rename affordances here (click-to-edit and the options menu's
  // `Rename`, which only focuses that same field) end in this mutation, so the
  // permission is checked once for the pair — matching `AutomationCardMenu`,
  // which gates its own rename on `RenameAutomation`.
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const { hasPermission } = usePermissions();
  const canRename =
    hasPermission(['RenameAutomation'], {
      organization: currentOrganizationId,
      workspace: currentWorkspaceId,
    }) && isTitleEditable;

  const isDeactivated =
    activationState ===
    V1AutomationActivationState.AUTOMATION_ACTIVATION_STATE_DEACTIVATED;

  return (
    <>
      <EditableTitle
        // Remounting is how the options menu's `Rename` gets the field into
        // edit mode: `autoFocusEdit` is only read on mount.
        key={`title-${renameRequestCount}`}
        value={title ?? ''}
        onRename={handleRename}
        isEditable={canRename && Boolean(automationId)}
        isDisabled={isRenaming}
        autoFocusEdit={shouldFocusTitle || renameRequestCount > 0}
        level="base"
        weight="medium"
        // Inside the document this is the page's main heading.
        headingLevel={1}
        // min-w-0 is what lets this shrink below its own text width: without
        // it the flex item keeps `min-width: auto`, so the bar overflows
        // instead of the name ellipsising.
        className={cn('min-w-0', titleClassName)}
        inputName="automationTitle"
        inputId="automation-title"
        textTestId="automation-title-text"
      />

      {showMenu && (
        <DocumentTitleMenu
          automationId={automationId}
          title={title ?? ''}
          isDraft={stage !== 'published'}
          version={version}
          onRename={
            isTitleEditable && automationId
              ? () => setRenameRequestCount(count => count + 1)
              : undefined
          }
          onExport={onExport}
          exportManifest={exportManifest}
          exportTitleFallback={exportTitleFallback}
        />
      )}

      {/* The document owns its own stage indicator: the page header shows a
          dot beside the back button, but the document omits that chrome, so
          without this the document carries no stage at all. */}
      {stageSlot}
      {!stageSlot && (
        <AutomationStageBadge
          stage={stage}
          isDeactivated={isDeactivated}
          className="ml-1.5 shrink-0"
          data-testid={`automation-stage-badge--${stage}`}
        />
      )}

      {isDeactivated && <DeactivatedBadge />}
    </>
  );
}

/**
 * Props for {@link AutomationDocumentStatus}.
 */
export interface IAutomationDocumentStatusProps {
  /** Lifecycle stage, which picks the meta text. */
  stage?: TAutomationStage;
  /** ISO timestamp of the last save, shown on the draft + blueprint stages. */
  lastSavedAt?: string;
  /**
   * Published version number (without the `v`), shown on the published
   * stage. Omit to show only the date.
   */
  publishedVersion?: string;
  /** ISO timestamp of the publish, shown on the published stage. */
  publishedAt?: string;
  /**
   * Set while a run is in flight; replaces the stage's meta text. `executing`
   * draws a pulsing `--primary` dot, `awaiting-guidance` a static `--warning`
   * one.
   */
  running?: TAutomationRunningState;
  /**
   * Replaces the stage's meta line with content the caller owns — the
   * blueprint's building / build-ended / staged-review states.
   */
  status?: ReactNode;
}

/**
 * The document's one status line: the caller's own status, else the live run,
 * else the stage's meta ("Last saved at …", "v4 · Published …").
 */
export function AutomationDocumentStatus({
  stage = 'draft',
  lastSavedAt,
  publishedVersion,
  publishedAt,
  running,
  status,
}: IAutomationDocumentStatusProps) {
  const { t: tAutomation } = useTranslation('automation');
  const { t: tDrafts } = useTranslation('drafts');

  const lastSaved = lastSavedAt
    ? tDrafts('drafts.list.lastSavedAt', {
        time: formatTimestamp(lastSavedAt, 'h:mm A'),
      })
    : undefined;

  // A run that has not reported a step yet is named without one: "Step 1"
  // would be a claim that the first step is already in flight.
  const runningLabel = (() => {
    if (!running) return undefined;
    if (running.state === 'step-test') {
      return tAutomation('automation.documentTitleBar.stepTest');
    }
    if (running.state === 'executing') {
      return running.step === undefined
        ? tAutomation('automation.documentTitleBar.runningNoStep')
        : tAutomation('automation.documentTitleBar.running', {
            step: running.step,
            total: running.totalSteps,
          });
    }
    return running.step === undefined
      ? tAutomation('automation.documentTitleBar.awaitingGuidanceNoStep')
      : tAutomation('automation.documentTitleBar.awaitingGuidance', {
          step: running.step,
        });
  })();

  // While the automation query is still in flight the bar shows nothing
  // rather than a half sentence like "Published ".
  const metaText = (() => {
    if (stage === 'published') {
      if (!publishedAt) return undefined;
      const date = formatTimestamp(publishedAt, 'MMM D, YYYY');
      return publishedVersion
        ? tAutomation('automation.documentTitleBar.publishedVersion', {
            version: publishedVersion,
            date,
          })
        : tAutomation('automation.documentTitleBar.published', { date });
    }
    if (stage === 'blueprint') {
      return [tAutomation('automation.documentTitleBar.notBuiltYet'), lastSaved]
        .filter(Boolean)
        .join(' · ');
    }
    return lastSaved;
  })();

  // `step-test` stays toneless: the sandbox is a place you are in, not a run
  // of this automation in flight, so it reads as plain meta text.
  const statusTone: 'idle' | 'running' | 'paused' =
    !running || running.state === 'step-test'
      ? 'idle'
      : running.state === 'executing'
        ? 'running'
        : 'paused';
  const statusText = running ? runningLabel : metaText;

  if (status && !running) {
    return (
      <Flex align="center" gap={2} className="min-w-0">
        {status}
      </Flex>
    );
  }
  if (!statusText) return null;
  return (
    <Flex align="center" gap={1.5} className="min-w-0">
      {statusTone !== 'idle' && (
        <span
          aria-hidden
          data-testid={`document-status-dot--${statusTone}`}
          className={cn(
            'size-1.5 shrink-0 rounded-full',
            statusTone === 'running'
              ? 'animate-pulse bg-primary'
              : 'bg-warning',
          )}
        />
      )}
      <Text
        data-testid="last-saved-at"
        color="muted"
        className="truncate whitespace-nowrap text-xs leading-4"
      >
        {statusText}
      </Text>
    </Flex>
  );
}
